import { AfterViewInit, Component, Inject, ViewChild, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';

import { HttpResponse } from '@angular/common/http';
import { NgxSpinnerService } from 'ngx-spinner';
import { saveAs } from 'file-saver-es';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, Validators, FormBuilder, FormGroup } from '@angular/forms';
import { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { InfoRequestService } from './info-request.service';
import { fadeInAnimation } from '../_animations/fade-in.animation';
import { AuthService } from '../auth/auth.service';
import { SessionService } from '../session/session.service';
import { FileService } from '../file/file.service';
import { ErrorModalService } from '../modal/error.modal.service';
import { AttestService } from '../attest/attest.service';
import { FiliDb } from '../interfaces/filiDb.interface';
import { HuvudB2 } from '../interfaces/huvudB2.interface';
import { NgbdB2BilagorModal } from '../modal/b2-bilagor.modal.component';
import { NotificationModalComponent } from '../modal/notification.modal.component';
import { InformationRequestDetailed } from '../interfaces/informationRequestDetailed.interface';
import { FileUploadComponent } from '../file/fileUpload/file-upload/file-upload.component';
import { SharedStateProvider } from '../sharedStateProvider/shared-state-provider';

@Component({
    selector: 'app-info-request',
    templateUrl: './info-request.component.html',
    styleUrls: ['./info-request.component.scss'],
    animations: [
        fadeInAnimation
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class InfoRequestComponent implements AfterViewInit {

  constructor(
    protected service: InfoRequestService,
    protected auth: AuthService,
    protected spinner: NgxSpinnerService,
    protected sessionService: SessionService,
    protected errorModalService: ErrorModalService,
    protected fileService: FileService,
    protected attestService: AttestService,
    protected route: ActivatedRoute,
    protected fb: FormBuilder,
    protected modalService: NgbModal,
    protected router: Router,
    private sharedStateProvider: SharedStateProvider,
    @Inject(DOCUMENT) private readonly document: Document) {
    this.loadRequest(this.route.snapshot.paramMap.get('id'));
  }

  public get messageMaxLength(): number { return 6000 };
  public get antalMeddelandenSomVisas(): number { return 4 };
  public get isLoaded(): boolean { return !!this.infoRequest && this.init };
  private init: boolean;
  public infoRequest: InformationRequestDetailed;
  private lockedWindow: NgbModalRef;
  public userForm: FormGroup;
  public visaAllaMeddelanden: boolean = false;

  public get messageForm() { return this.userForm.get('message') };
  public get message() { return this.messageForm.value; }
  public set message(value: string) { this.messageForm.setValue(value); }


  @ViewChild(FileUploadComponent) fileUpload!: FileUploadComponent;
  public get files() { return this.fileUpload?.files ?? []; }

  ngAfterViewInit() {
    this.init = true;
  }

  private loadRequest(id: string): void {
    const doLoad = () => {
      setTimeout(() => {
        if (!this.infoRequest) {
          this.spinner.show();
        }
      }, 300);

      this.service.getInfoRequest(id).subscribe(
        (infoRequest: InformationRequestDetailed) => {
          this.spinner.hide();
          this.infoRequest = infoRequest;
          this.message = '';
        },
        (error) => {
          this.spinner.hide();
          this.errorModalService.open('Inläsningsfel', 'Kunde inte hämta det begärda meddelandet.');
        });
    }

    if (!this.sessionService.hasSession) {
      this.router.navigate(['/login']);
      return;
    }

    doLoad();
  }

  openNotificationModal() {
    const modalRef = this.modalService.open(NotificationModalComponent, { centered: true });

    modalRef.componentInstance.title = 'modal.sent-title';
    modalRef.componentInstance.message = 'modal.sent-message';
    modalRef.componentInstance.okText = 'modal.notis-ok';
  }

  public reloadRequest() {
    this.spinner.show();
    this.loadRequest(this.infoRequest.id);

      this.openNotificationModal();
  }

  public ngOnInit(): void {
    this.userForm = this.fb.group({ 'message': new FormControl('', Validators.compose([Validators.maxLength(this.messageMaxLength), Validators.required])) });
  }

  public createMessage(): void {
    this.spinner.show();
    const id = this.infoRequest.id;
    const message = this.message;
    const files = this.files;

    this.service.sendResponse(id, message, files).subscribe(
      (isCommitted: boolean) => {
        this.reloadRequest();
        this.spinner.hide();
        this.fileUpload.clear();
        if (!this.infoRequest.answered)
          this.sharedStateProvider.decreaseWaitingRequestNr();
      },
      (error) => {
        this.errorModalService.open('Inläsningsfel', 'Misslyckades att skicka svar');
        this.spinner.hide();
      });
  }

  public showAttachments(b2: HuvudB2): void {
    this.openModal(NgbdB2BilagorModal, { centered: true });
    this.lockedWindow.componentInstance.arende = this.infoRequest.requestFirm;
    this.lockedWindow.componentInstance.b2 = b2;
  }

  private openModal(content, windowClass?: NgbModalOptions): void {
    this.closeModal(this.lockedWindow);
    this.lockedWindow = this.modalService.open(content, windowClass);
  }

  private closeModal(modalRef: NgbModalRef): void {
    if (!!modalRef) {
      modalRef.close();
    }
  }

  public getFile(file: FiliDb): void {
    this.spinner.show();

    this.fileService.getFile(this.sessionService.activeInstance.id, file.id).subscribe(
      (response: HttpResponse<ArrayBuffer>) => {
        this.spinner.hide();
        const contentType = response.headers.get('content-type');
        const blob = new Blob([response.body], { type: contentType });
        saveAs(blob, file.filnamn + file.filtillagg);
      },
      (error) => {
        this.errorModalService.open('Inläsningsfel', 'Kunde inte hämta den begärda filen.');
        this.spinner.hide();
      });
  }
}
